import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction';
import type { vtkColorTransferFunction as VtkCTF } from '@kitware/vtk.js';

/**
 * Fast inversion using getTable + setTable on a NEW instance
 * - Samples the source CTF over its scalar range
 * - Inverts RGB per sample (r,g,b => 1-r,1-g,1-b)
 * - Rebuilds a new CTF from the inverted table using buildFunctionFromTable
 */
export function invertColorTransferFunction(src: Nullable<VtkCTF>): VtkCTF | null {
  if (!src) return null;
  // Determine range from first/last node
  const n = src.getSize();
  let min = 0;
  let max = 1;

  if (n === 0) {
    // no nodes, return a default CTF
    const inverted: VtkCTF = vtkColorTransferFunction.newInstance();
    inverted.addRGBPoint(0, 0, 0, 0);
    inverted.addRGBPoint(1, 1, 1, 1);
    return inverted;
  }
  const first: number[] = [0, 0, 0, 0, 0, 0];
  src.getNodeValue(0, first);
  min = first[0];
  const last: number[] = [0, 0, 0, 0, 0, 0];
  src.getNodeValue(n - 1, last);
  max = last[0];

  // Sample, invert, and rebuild
  const table = [] as number[];
  src.getTable(min, max, n, table);
  const samplesInTable = table.length / 3;
  for (let i = 0; i < samplesInTable; i++) {
    const base = i * 3;
    table[base] = 1 - table[base];
    table[base + 1] = 1 - table[base + 1];
    table[base + 2] = 1 - table[base + 2];
  }

  // Build a NEW transfer function from the inverted table to avoid mutating src
  const inverted: VtkCTF = vtkColorTransferFunction.newInstance();
  inverted.buildFunctionFromTable(min, max, samplesInTable, table);
  return inverted;
}
